import { getAccountDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/components/BankAccounts.utils.ts';
import { asyncLoadExceljs } from '@dealstack/frontend/shared/utils/asyncLoadExceljs';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { BankAccountExportRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/bankAccountsExport.types.ts';
import { DataMipInvestorBankAccountRes } from '@dealstack/shared/lib/generated';

export const toExportRows = (
	bankAccounts: DataMipInvestorBankAccountRes[],
	labels: {
		validation: (row: DataMipInvestorBankAccountRes) => string;
		status: (row: DataMipInvestorBankAccountRes) => string;
	}
): BankAccountExportRow[] =>
	bankAccounts.map((account) => ({
		accountHolder: account.accountHolder,
		bankName: account.bankName ?? '',
		country: account.countryCode ?? '',
		currency: account.currency ?? '',
		// Newline-joined for the table cell; a spreadsheet cell wants one line.
		// One spreadsheet cell, so the per-line details join rather than wrap.
		accountDetails: getAccountDetails(account).detailLines.join(' · '),
		validation: labels.validation(account),
		verificationStatus: labels.status(account),
		owner: account.ownerName,
		ownerEmail: account.ownerEmail ?? '',
		lastUpdated: formatStringDateToUI(account.updatedAt, DATE_FORMATS.CALENDAR_DATE) ?? '',
	}));

export const buildBankAccountsWorkbookBlob = async (
	rows: BankAccountExportRow[],
	headers: Record<keyof BankAccountExportRow, string>
): Promise<Blob> => {
	const ExcelJS = await asyncLoadExceljs();
	const workbook = new ExcelJS.Workbook();
	workbook.created = new Date();
	const worksheet = workbook.addWorksheet('Bank accounts');

	worksheet.columns = (Object.keys(headers) as (keyof BankAccountExportRow)[]).map((key) => ({
		header: headers[key],
		key,
		width: 28,
	}));
	worksheet.addRows(rows);

	const buffer = await workbook.xlsx.writeBuffer();

	return new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
};
